/**
 * tutorRoomRules — les règles pures de la salle du tuteur, portées de l'ancien live à l'identique
 * (`ports/CLAUDE.md` § 5) : le crochet (`useTutorLive`) les lit, les tests les rejouent.
 *
 *  - **les minuteurs de 5 s** (`timerUpdates`) : `LiveMainLayout.js:63-114` ;
 *  - **les métriques** (`metricsRead`) : `LiveMainLayout.js:142-163`, `no_cache=1` quand un id absent du
 *    relevé PRÉCÉDENT des présents arrive (un élève qui part et revient le relance ; mon propre id, compté
 *    dans `liveParticipants`, aussi au premier relevé) ;
 *  - **l'avis « Aucun élève connecté »** (`noStudentYetFrom`) : `TutorLiveScreen.js:275-304` ;
 *  - **le débordement sur le live suivant** (`overrunToShow`) : `TutorOverrunDialog.js` ;
 *  - **la piste d'un participant** (`tracksOfParticipant`) : `TutorRoomView.js:62-72`,
 *    `TutorLiveScreen.js:212-220`.
 *  - **le jingle d'un résumé du champion** (`summariesToChime`, `announcedSummaryId`, `summariesNotAnnounced`) :
 *    `LiveParticipant.js:284-299`, moins ce que l'écouteur global joue déjà (`useLiveKitEventListener.js:398-405`).
 *  - **le navigateur** que nomme la boîte des permissions (`browserOf`) : `PermissionsManager.js:55-67`. Ici
 *    et non dans `tutorMediaAccess` : l'écran le lit sans charger les permissions d'Expo ;
 *  - **la salle qui attend CallKeep** (`awaitingCallKeep`) : le verrou de `common/livekit/LiveKitRoomM.js:11-17`.
 */
import moment from "moment"

import { closestNextOccurrence, type LiveSessionTimeLike } from "../student/liveSessionTime"

/* ── les minuteurs ── */

export type TimerInput = {
    /** `liveMode.timesSinceLastCall` : secondes depuis le dernier échange, par id (clés en chaîne). */
    timesSinceLastCall?: Record<string, number> | unknown[] | null
    /** `liveMode.activeTime` : le temps d'échange, `[{ id, time }]`. */
    activeTime?: { id: number; time: number }[] | null
    soundModeIds?: number[] | null
    /** `liveMode.liveParticipants` : les ids présents dans la salle (le tuteur compris). */
    participants?: number[] | null
    /** Une bascule de mode est en cours (`isSoundModeProcessing`) : le minuteur se tait. */
    soundModeProcessing?: boolean
}

export type TimerUpdates = {
    timesSinceLastCallUpdates: { participantId: number; timesSinceLastCall: number }[]
    activeTimeUpdates: { participantId: number; activeTime: number }[]
}

/**
 * Ce que le minuteur de 5 s pousse (`batchUpdateTimers`), ou `null` quand il n'a rien à pousser —
 * `LiveMainLayout.js:63-111` à l'identique : « depuis le dernier échange » des présents en Concentré,
 * « temps d'échange » des présents en Dialogue, rien pendant une bascule de mode.
 */
export function timerUpdates(input: TimerInput): TimerUpdates | null {
    if (input.soundModeProcessing) return null
    const soundModeIds = input.soundModeIds ?? []
    const participants = input.participants ?? []
    const timesSinceLastCallUpdates: TimerUpdates["timesSinceLastCallUpdates"] = []
    const activeTimeUpdates: TimerUpdates["activeTimeUpdates"] = []

    const since = input.timesSinceLastCall
    if (since != null && typeof since === "object") {
        for (const userId of Object.keys(since)) {
            const id = parseInt(userId, 10)
            const inSoundMode = soundModeIds.includes(id)
            const online = participants.includes(id)
            if (online && !inSoundMode) {
                timesSinceLastCallUpdates.push({
                    participantId: id,
                    timesSinceLastCall: (since as Record<string, number>)[userId],
                })
            }
        }
    }

    for (const entry of input.activeTime ?? []) {
        const inSoundMode = soundModeIds.includes(entry.id)
        const online = participants.includes(entry.id)
        if (inSoundMode && online) activeTimeUpdates.push({ participantId: entry.id, activeTime: entry.time })
    }

    if (timesSinceLastCallUpdates.length === 0 && activeTimeUpdates.length === 0) return null
    return { timesSinceLastCallUpdates, activeTimeUpdates }
}

/* ── les métriques ── */

/**
 * Le relevé des métriques à faire quand les présents changent (`LiveMainLayout.js:142-163`) : aucun
 * sans présent ; avec `no_cache` quand un id manque au relevé précédent. Rend aussi le relevé suivant.
 */
export function metricsRead(
    previous: ReadonlySet<number>,
    participants: readonly number[] | null | undefined
): { read: false } | { read: true; noCache: boolean; next: Set<number> } {
    if (participants == null || participants.length === 0) return { read: false }
    const noCache = participants.some((id) => !previous.has(id))
    return { read: true, noCache, next: new Set(participants) }
}

/** Les paramètres de `getMetricsV2`, tels que l'ancien les passait (`{ no_cache: "1" }` ou `{}`). */
export function metricsParams(noCache: boolean): Record<string, string> {
    return noCache ? { no_cache: "1" } : {}
}

/* ── « Aucun élève connecté » ── */

/** Le délai après le début de la séance au-delà duquel l'avis paraît (`TutorLiveScreen.js:304`). */
export const NO_STUDENT_GRACE_MS = 3 * 60 * 1000

export type NoticeSession = LiveSessionTimeLike & {
    users?: { id?: number | string | null; is_tutor?: boolean | null }[] | null
    live_room?: { type?: string | null } | null
}

/** Les élèves inscrits (tout membre qui n'est pas tuteur), comme `studentParticipants` de l'ancien. */
export function enrolledStudentIds(session: NoticeSession | null | undefined): string[] {
    return (session?.users ?? [])
        .filter((user) => user != null && user.is_tutor !== true && user.id != null)
        .map((user) => String(user.id))
}

/**
 * L'instant (ms) à partir duquel « Aucun élève connecté » peut paraître : trois minutes après le début
 * de la prochaine occurrence non finalisée ; `null` quand il ne paraît jamais (salle `trial`, pas
 * d'occurrence, pas d'élève inscrit). `didAnyStudentConnect` de l'ancien est toujours faux
 * (`todayLiveSessionLogs` n'est pas lu par l'écran) : il n'est pas repris, l'avis reparaît donc aussi
 * quand des élèves sont venus puis partis, comme aujourd'hui.
 */
export function noStudentYetFrom(session: NoticeSession | null | undefined): number | null {
    if (session == null || session.live_room?.type === "trial") return null
    if (enrolledStudentIds(session).length === 0) return null
    const date = closestNextOccurrence(session)?.date
    if (date == null) return null
    const start = moment(date)
    return start.isValid() ? start.valueOf() + NO_STUDENT_GRACE_MS : null
}

/* ── le débordement ── */

/** `live_signals.tutor_overrun` du relevé. */
export type ApiTutorOverrun = {
    next_occurrence_id?: number | string | null
    next_occurrence_label?: string | null
    p1_names?: string[] | null
    p2_names?: string[] | null
}

/**
 * Le débordement à montrer, ou `null` : une fois par occurrence suivante, l'acquit fait foi (persisté,
 * `tutorOverrunAck`) ; rien quand personne n'attend ni n'est attendu (`buildTutorOverrunCopy` rendait
 * `null`).
 */
export function overrunToShow(
    overrun: ApiTutorOverrun | null | undefined,
    acknowledged: (occurrenceId: number | string) => boolean
): (ApiTutorOverrun & { next_occurrence_id: number | string }) | null {
    const occurrenceId = overrun?.next_occurrence_id
    if (overrun == null || occurrenceId == null || occurrenceId === "") return null
    if (acknowledged(occurrenceId)) return null
    if ((overrun.p1_names ?? []).length === 0 && (overrun.p2_names ?? []).length === 0) return null
    return { ...overrun, next_occurrence_id: occurrenceId }
}

/* ── les pistes ── */

/** Ce que la sélection lit d'une piste de LiveKit. */
export type TrackLike = {
    source: string
    identity: string | null | undefined
    muted: boolean
}

/**
 * Les pistes d'un participant, côte à côte dans sa tuile (S-101) : sa caméra et son écran partagé, de
 * chacun de ses appareils (l'identité `id.hash` ramenée à l'id), dans l'ordre de la salle. Une piste
 * coupée n'est pas montrée : la tuile garde alors le visage.
 */
export function tracksOfParticipant<T extends TrackLike>(tracks: readonly T[], participantId: string): T[] {
    return tracks.filter(
        (track) =>
            !track.muted &&
            (track.source === "camera" || track.source === "screen_share") &&
            track.identity?.split(".")?.[0] === participantId
    )
}

/* ── le jingle du résumé du champion ── */

/** Une entrée de `liveMode.completedConversations` (la plus récente d'abord). */
export type CompletedConversationLike = {
    studentId?: number | string | null
    conversation?: { id?: number | string | null } | null
}

/**
 * Les conversations terminées qui font jouer le jingle (`LiveParticipant.js:284-299`) : pour chaque
 * membre de la séance qui a une tuile, la première de la file à son nom (`useCompletedConversation`),
 * quand elle n'a pas encore sonné. L'ancien la jouait une fois PAR TUILE montée (deux fois en focus,
 * le rail et le centre) : ici une fois.
 */
export function summariesToChime(
    completed: readonly CompletedConversationLike[] | null | undefined,
    memberIds: readonly string[],
    chimed: ReadonlySet<string>
): string[] {
    if (!Array.isArray(completed)) return []
    const fresh: string[] = []
    for (const memberId of memberIds) {
        const first = completed.find((entry) => entry?.studentId != null && String(entry.studentId) === memberId)
        const id = first?.conversation?.id
        if (id == null) continue
        const key = String(id)
        if (!chimed.has(key) && !fresh.includes(key)) fresh.push(key)
    }
    return fresh
}

/**
 * La conversation dont l'écouteur global (`LKEvents`, `useLiveKitEventListener.js:391-405`) joue déjà le jingle :
 * un `agentConversationCompleted` qui porte un résumé (`hasSummaryToShow` : `summary` non vide). `null` pour tout
 * autre message.
 */
export function announcedSummaryId(event: { type?: string } & Record<string, unknown>): string | null {
    if (event?.type !== "agentConversationCompleted") return null
    const conversation = (event.conversation as { conversation?: { id?: unknown; summary?: unknown } } | undefined)
        ?.conversation
    const summary = typeof conversation?.summary === "string" ? conversation.summary.trim() : ""
    const id = conversation?.id
    if (summary === "" || (typeof id !== "number" && typeof id !== "string")) return null
    return String(id)
}

/**
 * Parmi les résumés à sonner, ceux que l'écouteur global n'a pas sonnés : la file
 * (`completedConversations`) n'admet que des conversations à résumé (`admitCompleted`), et l'écouteur joue le
 * jingle de chacune qui arrive par `agentConversationCompleted`. Reste celle que la relecture de la séance
 * découvre terminée sans ce message (un point de départ fini avant que l'élève rejoigne la salle,
 * `getLiveSessionV2` du réducteur `liveMode`), et celles déjà dans la file à l'entrée de la salle, que la
 * tuile de l'ancien sonnait à son montage.
 */
export function summariesNotAnnounced(fresh: readonly string[], announced: ReadonlySet<string>): string[] {
    return fresh.filter((id) => !announced.has(id))
}

/* ── CallKeep ── */

/**
 * Le temps laissé à CallKeep pour démarrer une fois le jeton de salle là. Le service de premier plan
 * (Android) ou l'appel du système (iOS) démarre en une ou deux secondes ; au-delà de quinze, le tuteur
 * regarde « Connexion… » sans que rien ne se passe.
 */
export const CALL_KEEP_GRACE_MS = 15 * 1000

export type CallKeepGateInput = {
    isWeb: boolean
    /** `liveKitRoom.roomData.token` est là. */
    hasToken: boolean
    /** `liveMode.hasPhonePermission`. */
    hasPhonePermission: boolean | null | undefined
    /** `liveMode.isCallKeepOn`. */
    callKeepOn: boolean | null | undefined
}

/**
 * La salle attend-elle CallKeep pour se connecter ? Le verrou de `common/livekit/LiveKitRoomM.js:11-17`, lu
 * à l'identique (des valeurs vraies ou fausses) : au natif, la permission du téléphone accordée, `LiveKitRoom`
 * ne se connecte qu'une fois `isCallKeepOn`, même avec son jeton.
 */
export function awaitingCallKeep(input: CallKeepGateInput): boolean {
    return !input.isWeb && input.hasToken && !!input.hasPhonePermission && !input.callKeepOn
}

/* ── le navigateur ── */

/** Le navigateur, lu comme l'ancien (`PermissionsManager.js:55-67`, Edge se lit Chrome : il dit `chrome`). */
export type TutorBrowser = "Chrome" | "Firefox" | "Safari" | "Edge" | "other"

export function browserOf(userAgent: string | null | undefined): TutorBrowser {
    const agent = (userAgent ?? "").toLowerCase()
    if (agent.includes("chrome")) return "Chrome"
    if (agent.includes("firefox")) return "Firefox"
    if (agent.includes("safari")) return "Safari"
    if (agent.includes("edge")) return "Edge"
    return "other"
}
